import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { lambdaService, FileInfo, ProcessingFileInfo } from '../../services/lambdaService';
import { accessControlService, FileAccessGrant } from '../../services/accessControlService';
import { sheetsService, CorpusHit } from '../../services/sheetsService';
import { TopNav } from '../ui/top-nav';
import { APP_VERSION } from '../../version';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Files,
  Upload,
  BarChart3,
  Trash2,
  RefreshCw,
  Plus,
  Folder,
  FolderPlus,
  ChevronRight,
  X,
  Info,
  FileText,
  Search,
  MoreHorizontal,
  ArrowUpDown,
  Clock,
  HardDrive,
  Database,
  Layers,
  Mic,
  FileType2,
  Video,
  Image,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Helper function to remove ALL file extensions from display names
const getDisplayName = (fileName: string): string => {
  let name = fileName.replace(/\.[^.]*$/, '');
  if (/^\d+-/.test(name)) {
    name = name.replace(/^\d+-/, '');
  }
  name = name.replace(/_\d{8}_\d{6}$/, '');
  name = name.replace(/\s*\(\d+\s+files?\)$/i, '');
  // Strip extraction mode markers from image pipeline
  name = name.replace(/_?_TABLEMODE|_?_FIGUREPARSE|_?_DESCRIBE|_?_ALLMODE/g, '');
  return name;
};

// Helper to detect image extraction mode from raw file name
const getImageMode = (fileName: string): string | null => {
  if (/_?_TABLEMODE/i.test(fileName)) return 'Table';
  if (/_?_FIGUREPARSE/i.test(fileName)) return 'Figure';
  if (/_?_DESCRIBE/i.test(fileName)) return 'Describe';
  if (/_?_ALLMODE/i.test(fileName)) return 'All Modes';
  return null;
};

export const Dashboard: React.FC = () => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [processingFiles, setProcessingFiles] = useState<ProcessingFileInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [folders, setFolders] = useState<string[]>([]);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [moveFileDialogOpen, setMoveFileDialogOpen] = useState(false);
  const [fileToMove, setFileToMove] = useState<string | null>(null);
  const [targetFolder, setTargetFolder] = useState<string>('');
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [draggedFile, setDraggedFile] = useState<string | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [fileInfoDialogOpen, setFileInfoDialogOpen] = useState(false);
  const [fileForInfo, setFileForInfo] = useState<FileInfo | null>(null);
  const [fileDescription, setFileDescription] = useState('');
  const [fileSourceUrl, setFileSourceUrl] = useState('');
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null); // null = loading
  const [fileAccessGrants, setFileAccessGrants] = useState<FileAccessGrant[]>([]);
  const [userId, setUserId] = useState<string>('');
  const [corpusResults, setCorpusResults] = useState<Record<string, CorpusHit[]>>({});
  const [isCorpusSearching, setIsCorpusSearching] = useState(false);
  const [showCorpusResults, setShowCorpusResults] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadUserInfo();
    loadFiles();
    loadFolders();
    loadProcessingStatus();

    const interval = setInterval(loadProcessingStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadUserInfo = async () => {
    try {
      const user = await getCurrentUser();
      setUserEmail(user.signInDetails?.loginId || 'User');

      // Get user ID and check admin status
      const session = await fetchAuthSession();
      const currentUserId = session.tokens?.idToken?.payload?.sub as string;
      if (currentUserId) {
        setUserId(currentUserId);
        // Check if user is admin
        const permissionsResponse = await accessControlService.getUserPermissions(currentUserId);
        if (permissionsResponse.success && permissionsResponse.permissions) {
          const adminStatus = permissionsResponse.permissions?.admin === true;
          setIsAdmin(adminStatus);

          // If not admin, fetch file access grants
          if (!adminStatus) {
            const grantsResponse = await accessControlService.getFileAccess(currentUserId);
            if (grantsResponse.success && grantsResponse.grants) {
              setFileAccessGrants(grantsResponse.grants);
            } else {
              setFileAccessGrants([]);
            }
          }
        } else {
          // Default to non-admin if permissions fail
          setIsAdmin(false);
          setFileAccessGrants([]);
        }
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      setIsAdmin(false);
      setFileAccessGrants([]);
    }
  };

  const loadFiles = async () => {
    try {
      const response = await lambdaService.listFiles();
      if (response.success) {
        setFiles(response.files);
        if (response.error) {
          toast.info(response.error);
        }
      } else {
        toast.error(response.error || 'Unable to load files');
      }
    } catch (error) {
      toast.error('Unable to load files at this time');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProcessingStatus = async () => {
    try {
      const response = await lambdaService.getProcessingStatus();
      if (response.success) {
        setProcessingFiles(response.processing_files);
      }
    } catch (error) {
      // console.warn('Processing status error:', error);
    }
  };

  const refreshFiles = async () => {
    setIsRefreshing(true);
    await loadFiles();
    setIsRefreshing(false);
    toast.success('Files refreshed');
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      const success = await lambdaService.deleteFile(fileToDelete);
      if (success) {
        toast.success('File deleted successfully');
        setFiles(files.filter(f => f.fileName !== fileToDelete));
      } else {
        toast.error('Failed to delete file');
      }
    } catch (error) {
      toast.error('Error deleting file');
    } finally {
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const handleDeleteFailedUpload = async (s3Key: string, filename: string) => {
    try {
      const success = await lambdaService.deleteFailedUpload(s3Key);
      if (success) {
        toast.success(`Deleted failed upload: ${filename}`);
        setProcessingFiles(processingFiles.filter(f => f.s3_key !== s3Key));
        await loadProcessingStatus();
      } else {
        toast.error('Failed to delete upload');
      }
    } catch (error) {
      toast.error('Error deleting failed upload');
    }
  };

  const loadFolders = async () => {
    try {
      const result = await lambdaService.listFolders();
      if (result.success) {
        setFolders(result.folders);
      }
    } catch (error) {
      // console.error('Error loading folders:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name cannot be empty');
      return;
    }

    if (!/^[a-zA-Z0-9_\-. ]+$/.test(newFolderName)) {
      toast.error('Folder name can only contain letters, numbers, spaces, dashes, and underscores');
      return;
    }

    try {
      const folderPath = currentFolder
        ? `${currentFolder}/${newFolderName}`
        : newFolderName;

      const result = await lambdaService.createFolder(folderPath);
      if (result.success) {
        toast.success(`Folder "${newFolderName}" created successfully`);
        setNewFolderDialogOpen(false);
        setNewFolderName('');
        await loadFolders();
        await loadFiles();
      } else {
        toast.error(result.error || 'Failed to create folder');
      }
    } catch (error) {
      toast.error('Error creating folder');
    }
  };

  const handleMoveFile = async () => {
    if (!fileToMove) return;

    try {
      const result = await lambdaService.moveFile(fileToMove, targetFolder);
      if (result.success) {
        toast.success(`File moved to ${targetFolder || 'root folder'}`);
        setMoveFileDialogOpen(false);
        setFileToMove(null);
        setTargetFolder('');
        await loadFiles();
        await loadFolders();
      } else {
        toast.error(result.error || 'Failed to move file');
      }
    } catch (error) {
      toast.error('Error moving file');
    }
  };

  const handleNavigateToFolder = (folder: string) => {
    setCurrentFolder(folder);
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      const result = await lambdaService.deleteFolder(folderToDelete, true);
      if (result.success) {
        toast.success(`Folder "${folderToDelete}" deleted successfully`);
        setDeleteFolderDialogOpen(false);
        setFolderToDelete(null);
        await loadFolders();
        await loadFiles();

        if (currentFolder === folderToDelete || currentFolder.startsWith(folderToDelete + '/')) {
          setCurrentFolder('');
        }
      } else {
        toast.error(result.error || 'Failed to delete folder');
      }
    } catch (error) {
      toast.error('Error deleting folder');
    }
  };

  const handleSaveFileInfo = async () => {
    if (!fileForInfo) return;

    setIsSavingInfo(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        toast.error('Authentication required. Please sign in again.');
        setIsSavingInfo(false);
        return;
      }

      const response = await fetch('https://jtbb58634f.execute-api.us-east-1.amazonaws.com/prod/file-metadata', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          file_key: fileForInfo.s3Key || fileForInfo.fileName,
          description: fileDescription,
          source_url: fileSourceUrl,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('File info saved successfully');
        setFileInfoDialogOpen(false);
        await loadFiles();
      } else {
        toast.error(result.error || 'Failed to save file info');
      }
    } catch (error) {
      console.error('Error saving file info:', error);
      toast.error('Error saving file info');
    } finally {
      setIsSavingInfo(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, fileKey: string) => {
    setDraggedFile(fileKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, folderName?: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderName || '');
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e: React.DragEvent, targetFolderName?: string) => {
    e.preventDefault();
    setDragOverFolder(null);

    if (!draggedFile) return;

    const targetFolderPath = targetFolderName
      ? (currentFolder ? `${currentFolder}/${targetFolderName}` : targetFolderName)
      : currentFolder;

    try {
      const result = await lambdaService.moveFile(draggedFile, targetFolderPath);
      if (result.success) {
        toast.success(`File moved to ${targetFolderPath || 'root folder'}`);
        await loadFiles();
        await loadFolders();
      } else {
        toast.error(result.error || 'Failed to move file');
      }
    } catch (error) {
      toast.error('Error moving file');
    } finally {
      setDraggedFile(null);
    }
  };

  const getBreadcrumbs = () => {
    if (!currentFolder) return [];
    return currentFolder.split('/');
  };

  const handleCorpusSearch = async () => {
    if (!searchTerm.trim() || !userId) return;
    setIsCorpusSearching(true);
    setShowCorpusResults(true);
    setCorpusResults({});
    try {
      const response = await sheetsService.corpusSearch(userId, searchTerm.trim());
      if (response.status === 'complete') {
        setCorpusResults(response.results || {});
        // Auto-build indexes if no indexes were searched
        if (response.indexes_searched === 0) {
          toast.info('Building search indexes for your content. This may take a moment...');
          await sheetsService.buildCorpusIndexes(userId);
          // Re-search after a short delay to allow indexes to build
          setTimeout(async () => {
            try {
              const retryResponse = await sheetsService.corpusSearch(userId, searchTerm.trim());
              if (retryResponse.status === 'complete') {
                setCorpusResults(retryResponse.results || {});
              }
            } catch {
              // Ignore retry errors
            }
          }, 5000);
        }
      } else if (response.error) {
        toast.error(response.error);
      }
    } catch (error) {
      console.error('Corpus search error:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setIsCorpusSearching(false);
    }
  };

  // Filter files based on file access grants (for non-admin users)
  const filterByFileAccess = (fileList: FileInfo[]): FileInfo[] => {
    // Admins see all files
    if (isAdmin === true) return fileList;
    // Still loading admin status
    if (isAdmin === null) return fileList;
    // Non-admin with no grants sees no files
    if (fileAccessGrants.length === 0) return [];

    return fileList.filter(file => {
      const key = file.s3Key || file.fileName;

      for (const grant of fileAccessGrants) {
        if (grant.type === 'folder') {
          // Folder grant: check prefix match
          if (key.startsWith(grant.path)) {
            return true;
          }
        } else {
          // File grant: exact match
          if (key === grant.path) {
            return true;
          }
        }
      }
      return false;
    });
  };

  const getFilesInCurrentFolder = () => {
    // First apply file access filtering for non-admins
    const accessFiltered = filterByFileAccess(files);

    const filtered = accessFiltered.filter(file => {
      const key = file.s3Key || file.fileName;
      let relativePath = '';
      if (key.includes('/data/')) {
        relativePath = key.substring(key.indexOf('/data/') + 6);
      } else if (key.includes('/uploads/')) {
        relativePath = key.substring(key.indexOf('/uploads/') + 9);
      } else {
        relativePath = key.split('/').pop() || key;
      }

      if (!currentFolder) {
        return !relativePath.includes('/');
      } else {
        if (!relativePath.startsWith(currentFolder + '/')) {
          return false;
        }
        const afterFolder = relativePath.substring(currentFolder.length + 1);
        return !afterFolder.includes('/');
      }
    });

    if (!searchTerm.trim()) return filtered;

    return filtered.filter(file =>
      getDisplayName(file.fileName).toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getFoldersInCurrentFolder = () => {
    const fileNames = files.map(file => {
      const key = file.s3Key || file.fileName;
      const parts = key.split('/');
      return parts[parts.length - 1];
    });

    let folderList: string[] = [];

    if (!currentFolder) {
      folderList = folders
        .filter(f => !f.includes('/'))
        .filter(f => !fileNames.includes(f))
        .sort();
    } else {
      const depth = currentFolder.split('/').length;
      folderList = folders
        .filter(f => f.startsWith(currentFolder + '/'))
        .map(f => {
          const parts = f.split('/');
          if (parts.length === depth + 1) {
            return parts[depth];
          }
          return null;
        })
        .filter((f, i, arr) => f && arr.indexOf(f) === i)
        .filter(f => !fileNames.includes(f as string))
        .sort() as string[];
    }

    if (!searchTerm.trim()) return folderList;

    return folderList.filter(folder =>
      folder.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <TopNav userEmail={userEmail} />
      <main className="overflow-auto p-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
        <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Files className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-base font-semibold text-foreground">File Management</h1>
              {/* Breadcrumb navigation */}
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <button
                  className="hover:text-foreground transition-colors"
                  onClick={() => setCurrentFolder('')}
                >
                  All Files
                </button>
                {getBreadcrumbs().map((folder, index) => {
                  const path = getBreadcrumbs().slice(0, index + 1).join('/');
                  return (
                    <React.Fragment key={path}>
                      <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                      <button
                        className="hover:text-foreground transition-colors"
                        onClick={() => setCurrentFolder(path)}
                      >
                        {folder}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative hidden md:block w-80">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search across audio, PDF and image content"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    handleCorpusSearch();
                  }
                }}
                className="pl-9 pr-24 h-10"
              />
              {searchTerm.trim() && (
                <span className="absolute right-3 top-2.5 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  Enter to search
                </span>
              )}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={refreshFiles}
              disabled={isRefreshing}
              title="Refresh"
              className="h-10 w-10"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="default" size="default" className="gap-2 h-10 px-4">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">New</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setNewFolderDialogOpen(true)}>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  New Folder
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/upload" state={{ targetFolder: currentFolder }}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload File
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
          {/* Processing Status Section */}
          {processingFiles.length > 0 && (
            <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <div className="p-1.5 rounded-full bg-blue-100 dark:bg-blue-900">
                    <RefreshCw className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                  </div>
                  Processing ({processingFiles.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid gap-3">
                  {processingFiles.map((file) => (
                    <div key={file.s3_key} className="p-3 rounded-lg bg-background border shadow-sm text-sm">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            {file.original_filename}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                            <span>{(file.file_size / (1024 * 1024)).toFixed(1)} MB</span>
                            {file.chunk_info && (
                              <>
                                <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                <span>{file.chunk_info.current_chunks}/{file.chunk_info.expected_chunks} chunks</span>
                                {file.chunk_info.progress_percent !== undefined && (
                                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                                    ({file.chunk_info.progress_percent.toFixed(0)}%)
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${file.status === 'processing'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                            }`}>
                            {file.status === 'processing' ? 'Processing' : 'Failed'}
                          </span>
                          {file.status === 'failed' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDeleteFailedUpload(file.s3_key, file.original_filename)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {file.error_message && (
                        <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive border border-destructive/20">
                          <span className="font-medium">Error:</span> {file.error_message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Universal Corpus Search Results */}
          {showCorpusResults && (
            <Card className="mb-6 border-border/50 shadow-sm">
              <CardHeader className="pb-3 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base font-medium">
                    <Search className="h-4 w-4 text-primary" />
                    Content Search: "{searchTerm}"
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCorpusResults(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {isCorpusSearching ? (
                  <div className="flex items-center justify-center py-8 gap-3">
                    <div className="h-5 w-5 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
                    <span className="text-sm text-muted-foreground">Searching across your content...</span>
                  </div>
                ) : Object.keys(corpusResults).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">No results found for "{searchTerm}"</p>
                    <p className="text-xs mt-1">Try different keywords or check that your content has been processed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(['pdf', 'audio', 'image'] as const).map(corpusType => {
                      const hits = corpusResults[corpusType];
                      if (!hits || hits.length === 0) return null;

                      const typeConfig = {
                        pdf: { icon: FileType2, label: 'PDF Documents', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-500/10' },
                        audio: { icon: Mic, label: 'Audio / Video', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10' },
                        image: { icon: Image, label: 'Images', color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-500/10' },
                      }[corpusType];

                      const TypeIcon = typeConfig.icon;

                      return (
                        <div key={corpusType}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`p-1 rounded ${typeConfig.bg}`}>
                              <TypeIcon className={`h-3.5 w-3.5 ${typeConfig.color}`} />
                            </div>
                            <span className={`text-sm font-medium ${typeConfig.color}`}>
                              {typeConfig.label} ({hits.length})
                            </span>
                          </div>
                          <div className="space-y-2 ml-7">
                            {hits.slice(0, 10).map((hit, i) => (
                              <div
                                key={i}
                                className="p-3 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={() => {
                                  const dataset = hit.source_dataset || hit.source_file;
                                  if (dataset) {
                                    navigate(`/sheets?file=${encodeURIComponent(dataset)}`);
                                  }
                                }}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    {hit.source_dataset && (
                                      <span className="text-xs font-medium text-foreground">
                                        {hit.source_dataset}
                                      </span>
                                    )}
                                    {hit.source_file && hit.source_file !== hit.source_dataset && (
                                      <span className="text-xs text-muted-foreground ml-2">
                                        {hit.source_file}
                                      </span>
                                    )}
                                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                      {hit.content || hit.text || ''}
                                    </p>
                                    {corpusType === 'audio' && hit.start_time !== undefined && (
                                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                        <Clock className="h-3 w-3" />
                                        <span>
                                          {Math.floor(hit.start_time / 60)}:{String(Math.floor(hit.start_time % 60)).padStart(2, '0')}
                                        </span>
                                        {hit.speaker && (
                                          <>
                                            <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                                            <span>{hit.speaker}</span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {hits.length > 10 && (
                              <p className="text-xs text-muted-foreground ml-3">
                                +{hits.length - 10} more results
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
              <div className="relative">
                <div className="h-12 w-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              </div>
              <p className="text-muted-foreground font-medium animate-pulse">Loading your files...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[60vh]">
              <div className="bg-muted/30 p-8 rounded-full mb-6">
                <Files className="h-16 w-16 text-muted-foreground/50" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No files found</h3>
              <p className="text-muted-foreground max-w-sm text-center mb-8">
                Upload your first file to start analyzing data with BigSplice.
              </p>
              <Button size="lg" asChild className="gap-2">
                <Link to="/upload">
                  <Upload className="h-5 w-5" />
                  Upload File
                </Link>
              </Button>
            </div>
          ) : (
            <Card className="border-border/50 shadow-sm">
              <CardHeader className="pb-4 border-b border-border/50 bg-muted/5">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-medium">
                      {currentFolder ? currentFolder : 'All Files'}
                    </CardTitle>
                    <CardDescription>
                      {getFilesInCurrentFolder().length} files, {getFoldersInCurrentFolder().length} folders
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[40%]">Name</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Date Modified</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFoldersInCurrentFolder().length === 0 && getFilesInCurrentFolder().length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                          {isAdmin === false && fileAccessGrants.length === 0 ? (
                            <div className="space-y-2">
                              <p>You don't have access to any files yet.</p>
                              <p className="text-sm">Contact your administrator to request file access.</p>
                            </div>
                          ) : (
                            "No items found in this folder."
                          )}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Folders */}
                    {getFoldersInCurrentFolder().map((folder) => {
                      const fullFolderPath = currentFolder ? `${currentFolder}/${folder}` : folder;
                      const isDragOver = dragOverFolder === folder;

                      return (
                        <TableRow
                          key={folder}
                          className={`cursor-pointer group ${isDragOver ? 'bg-primary/10 border-primary' : ''}`}
                          onClick={() => handleNavigateToFolder(fullFolderPath)}
                          onDragOver={(e) => handleDragOver(e, folder)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, folder)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-500/10 rounded-md text-blue-600 dark:text-blue-400">
                                <Folder className="h-5 w-5" />
                              </div>
                              <span className="text-base">{folder}</span>
                              {isDragOver && (
                                <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full animate-pulse">
                                  Drop here
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">—</TableCell>
                          <TableCell className="text-muted-foreground">—</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFolderToDelete(fullFolderPath);
                                setDeleteFolderDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Files */}
                    {getFilesInCurrentFolder().map((file) => {
                      const isSqlFolder = file.isSqlImportFolder || file.type === 'sql-folder';
                      const isSqlFile = file.isSqlImport;

                      return (
                        <TableRow
                          key={file.s3Key || file.fileName}
                          draggable={!isSqlFolder}
                          onDragStart={(e) => !isSqlFolder && handleDragStart(e, file.s3Key || file.fileName)}
                          className={`cursor-pointer group hover:bg-muted/30 ${isSqlFolder ? 'border-l-[3px] border-l-violet-500 bg-gradient-to-r from-violet-500/5 to-transparent' : ''}`}
                          onClick={() => {
                            const key = file.s3Key || file.fileName;
                            let folderName = key;
                            if (key.includes('/data/')) {
                              const relativePath = key.substring(key.indexOf('/data/') + 6);
                              folderName = relativePath.split('/')[0];
                            }
                            folderName = folderName.replace('.parquet', '');
                            navigate(`/sheets?file=${encodeURIComponent(folderName)}`);
                          }}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3 min-w-0">
                              {isSqlFolder ? (
                                <div className="p-2 bg-violet-500/10 rounded-md text-violet-600 dark:text-violet-400">
                                  <Database className="h-5 w-5" />
                                </div>
                              ) : file.fileType === 'audio' ? (
                                <div className="p-2 bg-emerald-500/10 rounded-md text-emerald-600 dark:text-emerald-400">
                                  <Mic className="h-5 w-5" />
                                </div>
                              ) : file.fileType === 'pdf' ? (
                                <div className="p-2 bg-orange-500/10 rounded-md text-orange-600 dark:text-orange-400">
                                  <FileType2 className="h-5 w-5" />
                                </div>
                              ) : file.fileType === 'video' ? (
                                <div className="p-2 bg-purple-500/10 rounded-md text-purple-600 dark:text-purple-400">
                                  <Video className="h-5 w-5" />
                                </div>
                              ) : file.fileType === 'image' ? (
                                <div className="p-2 bg-teal-500/10 rounded-md text-teal-600 dark:text-teal-400">
                                  <Image className="h-5 w-5" />
                                </div>
                              ) : (
                                <div className={`p-2 rounded-md ${isSqlFile ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400' : 'bg-muted text-muted-foreground'}`}>
                                  <FileText className="h-5 w-5" />
                                </div>
                              )}
                              <div className="flex flex-col min-w-0">
                                <span className="truncate font-medium text-foreground" title={file.jobName || getDisplayName(file.fileName)}>
                                  {file.jobName || getDisplayName(file.fileName)}
                                </span>
                                {isSqlFolder && (
                                  <span className="inline-flex items-center text-[10px] text-violet-600 dark:text-violet-400 mt-0.5">
                                    <Layers className="h-3 w-3 mr-1" />
                                    {file.partCount} parts • {file.totalRows?.toLocaleString() || '?'} rows
                                  </span>
                                )}
                                {isSqlFile && !isSqlFolder && (
                                  <span className="inline-flex items-center text-[10px] text-violet-600 dark:text-violet-400 mt-0.5">
                                    <Database className="h-3 w-3 mr-1" />
                                    SQL Import
                                  </span>
                                )}
                                {file.fileType === 'audio' && (
                                  <span className="inline-flex items-center text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                                    <Mic className="h-3 w-3 mr-1" />
                                    Audio Transcript
                                  </span>
                                )}
                                {file.fileType === 'pdf' && (
                                  <span className="inline-flex items-center text-[10px] text-orange-600 dark:text-orange-400 mt-0.5">
                                    <FileType2 className="h-3 w-3 mr-1" />
                                    PDF Document
                                  </span>
                                )}
                                {file.fileType === 'video' && (
                                  <span className="inline-flex items-center text-[10px] text-purple-600 dark:text-purple-400 mt-0.5">
                                    <Video className="h-3 w-3 mr-1" />
                                    Video Transcript
                                  </span>
                                )}
                                {file.fileType === 'image' && (
                                  <span className="inline-flex items-center text-[10px] text-teal-600 dark:text-teal-400 mt-0.5">
                                    <Image className="h-3 w-3 mr-1" />
                                    Image{(() => { const mode = getImageMode(file.fileName); return mode ? ` • ${mode}` : ''; })()}
                                  </span>
                                )}
                                {!isSqlFolder && !isSqlFile && file.fileSize > 100 * 1024 * 1024 && (
                                  <span className="inline-flex items-center text-[10px] text-blue-600 dark:text-blue-400 mt-0.5">
                                    <HardDrive className="h-3 w-3 mr-1" />
                                    Large File
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground font-mono text-xs">
                            {formatFileSize(file.fileSize)}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(file.uploadDate)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                              {isSqlFolder ? (
                                <>
                                  {/* Compact button - disabled for now */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-700 cursor-not-allowed opacity-60"
                                    disabled
                                    title="Compact all parts into a single file (coming soon)"
                                  >
                                    <Layers className="h-3.5 w-3.5 mr-1.5" />
                                    Compact
                                    <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-violet-500/20 rounded">Soon</span>
                                  </Button>

                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                    onClick={() => {
                                      setFileToDelete(file.s3Key || file.fileName);
                                      setDeleteDialogOpen(true);
                                    }}
                                    title="Delete SQL Import Folder"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  {/* Analyze button hidden
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="h-8"
                                    asChild
                                  >
                                    <Link to={`/analyze?file=${encodeURIComponent(file.fileName)}`}>
                                      <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                                      Analyze
                                    </Link>
                                  </Button>
                                  */}

                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                    onClick={() => {
                                      setFileForInfo(file);
                                      setFileDescription((file as any).userDescription || '');
                                      setFileSourceUrl((file as any).sourceUrl || '');
                                      setFileInfoDialogOpen(true);
                                    }}
                                    title="Info & Metadata"
                                  >
                                    <Info className="h-4 w-4" />
                                  </Button>

                                  {!isSqlFile && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                      onClick={() => {
                                        setFileToMove(file.s3Key || file.fileName);
                                        setMoveFileDialogOpen(true);
                                      }}
                                      title="Move to Folder"
                                    >
                                      <Folder className="h-4 w-4" />
                                    </Button>
                                  )}

                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                    onClick={() => {
                                      setFileToDelete(file.fileName);
                                      setDeleteDialogOpen(true);
                                    }}
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{fileToDelete ? getDisplayName(fileToDelete) : ''}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFile}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Info dialog */}
      <Dialog open={fileInfoDialogOpen} onOpenChange={setFileInfoDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>File Information</DialogTitle>
            <DialogDescription>
              Add helpful context about this file for future reference and AI analysis
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="file-name">File Name</Label>
              <Input
                id="file-name"
                value={fileForInfo ? getDisplayName(fileForInfo.fileName) : ''}
                disabled
                className="bg-muted text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="source-url">Source URL (optional)</Label>
              <Input
                id="source-url"
                placeholder="e.g., https://example.com/data/file.csv"
                value={fileSourceUrl}
                onChange={(e) => setFileSourceUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                If this file was downloaded from a URL, paste the link here
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description & Context</Label>
              <Textarea
                id="description"
                placeholder="e.g., Customer transaction data from Q4 2024, contains sales records..."
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
                rows={6}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Add any context that would help someone understand this file. This info will be shown to AI when analyzing the file.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFileInfoDialogOpen(false)}
              disabled={isSavingInfo}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFileInfo}
              disabled={isSavingInfo}
            >
              {isSavingInfo ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New folder dialog */}
      <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              {currentFolder
                ? `Create a new folder inside "${currentFolder}"`
                : 'Create a new folder in the root directory'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder Name</Label>
              <Input
                id="folder-name"
                placeholder="e.g., reports, data, 2024"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Only letters, numbers, spaces, dashes, and underscores are allowed
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNewFolderDialogOpen(false);
                setNewFolderName('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move file dialog */}
      <Dialog open={moveFileDialogOpen} onOpenChange={setMoveFileDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move File</DialogTitle>
            <DialogDescription>
              Select a destination folder for "{fileToMove}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="target-folder">Destination Folder</Label>
              <select
                id="target-folder"
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                value={targetFolder}
                onChange={(e) => setTargetFolder(e.target.value)}
              >
                <option value="">Root folder</option>
                {folders.map((folder) => (
                  <option key={folder} value={folder}>
                    {folder}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setMoveFileDialogOpen(false);
                setFileToMove(null);
                setTargetFolder('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleMoveFile}>
              Move File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete folder dialog */}
      <Dialog open={deleteFolderDialogOpen} onOpenChange={setDeleteFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Folder</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{folderToDelete ? getDisplayName(folderToDelete) : ''}" and all its contents? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteFolderDialogOpen(false);
                setFolderToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFolder}
            >
              Delete Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};